import { lessOpinionatedCurrencyFormatter } from "@intelligems/ig-graphs/data-formatters";
import {
  ExperienceFe,
  LandingPageFilterItemType,
  LandingPageType,
} from "@intelligems/ig-types";
import {
  getFirstStartedAtTs,
  getLastEndedAtTs,
} from "@intelligems/ig-analytics";
import {
  camelToSentenceCase,
  capitalizeFirstLetter,
} from "@intelligems/ig-utils";

import { getConversionStageReachedList } from "@src/components/AudienceFilters/utils";
import { getLandingPageTypeLabel } from "@src/features/analytics/analyticsFilters/utils";
import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
} from "@src/store/types/dashboardFilters";
import { fullDateTimeConverter } from "@src/utils/dateConverters";

/**
 * Pure "given the current filters, does this chip exist and what does it say"
 * derivations: one per filter dimension, each fully computable from its
 * inputs with no dependency on any previously-rendered chip.
 */

export function getAnalyticsViewTypeChip(
  analyticsViewType: IExperienceDashboardFilters["analyticsViewType"],
  isUpsell?: boolean
): AnalyticsActiveFilterType | null {
  if (analyticsViewType === "sitewide_orders" || analyticsViewType === "none") {
    return null;
  }
  return {
    key: "analyticsViewType",
    value: true,
    label: `${
      analyticsViewType === "test_orders_only" && isUpsell
        ? "Upsell"
        : analyticsViewType === "test_orders_only"
          ? "Target"
          : "Specific"
    } Products Only`,
  };
}

export function getIsZScoreEnabledChip(
  isZScoreEnabled: boolean,
  maxZScore: IExperienceDashboardFilters["maxZScore"]
): AnalyticsActiveFilterType | null {
  if (!isZScoreEnabled) {
    return null;
  }
  return {
    key: "isZScoreEnabled",
    value: true,
    label: `Max Z-Score: ${maxZScore}`,
  };
}

export function getExcludeFreeOrdersChip(
  excludeFreeOrders: boolean
): AnalyticsActiveFilterType | null {
  if (excludeFreeOrders !== false) {
    return null;
  }
  return {
    key: "excludeFreeOrders",
    value: false,
    label: "Include Free Orders",
  };
}

export function getDeviceTypeChip(
  deviceType: IExperienceDashboardFilters["deviceType"]
): AnalyticsActiveFilterType | null {
  if (deviceType !== "desktop" && deviceType !== "mobile") {
    return null;
  }
  return {
    key: "deviceType",
    value: deviceType,
    label: capitalizeFirstLetter(deviceType),
  };
}

export function getCustomerTypeChip(
  customerType: IExperienceDashboardFilters["customerType"]
): AnalyticsActiveFilterType | null {
  if (customerType !== "new" && customerType !== "returning") {
    return null;
  }
  return {
    key: "customerType",
    value: customerType,
    label: `${capitalizeFirstLetter(customerType)} Customers`,
  };
}

export function getVisitorTypeChip(
  visitorType: IExperienceDashboardFilters["visitorType"]
): AnalyticsActiveFilterType | null {
  if (visitorType !== "new" && visitorType !== "returning") {
    return null;
  }
  return {
    key: "visitorType",
    value: visitorType,
    label: `${capitalizeFirstLetter(visitorType)} Visitors`,
  };
}

export function getCustomEventsChip(
  customEvents: string[] | undefined
): AnalyticsActiveFilterType | null {
  if (!customEvents?.length) {
    return null;
  }
  // Label is resolved downstream by getFilterLabel, which has access to
  // customMetrics (event id -> display name) that isn't available here.
  return { key: "customEvents", value: customEvents, label: "" };
}

export function getCountryCodesChip(
  countryCodes: string[] | undefined,
  countryCodesFilterType: IExperienceDashboardFilters["countryCodesFilterType"]
): AnalyticsActiveFilterType | null {
  if (!countryCodes?.length) {
    return null;
  }
  const countryCodesLabel =
    countryCodes.length > 3
      ? `${countryCodes.slice(0, 3).join(", ")} + ${countryCodes.length - 3}`
      : countryCodes.join(", ");
  const directionPrefix = countryCodesFilterType === "exclude" ? "Not " : "";
  return {
    key: "countryCodes",
    value: countryCodes,
    label: `${directionPrefix}${countryCodesLabel}`,
  };
}

export function getChannelTypeChip(
  channels: IExperienceDashboardFilters["channels"] | undefined
): AnalyticsActiveFilterType | null {
  if (!channels?.length) {
    return null;
  }
  const label =
    channels.length > 3
      ? `${channels.slice(0, 3).join(", ")} + ${channels.length - 3}`
      : channels.join(", ");
  return { key: "channels", value: channels, label };
}

export function getShippingMethodsChip(
  shippingMethods: string[] | undefined
): AnalyticsActiveFilterType | null {
  if (!shippingMethods?.length) {
    return null;
  }
  const label =
    shippingMethods.length > 3
      ? `${shippingMethods.slice(0, 3).join(", ")} + ${shippingMethods.length - 3}`
      : shippingMethods.join(", ");
  return { key: "shippingMethods", value: shippingMethods, label };
}

export function getReachedCheckoutStageChip(
  reachedCheckoutStage: IExperienceDashboardFilters["reachedCheckoutStage"]
): AnalyticsActiveFilterType | null {
  if (reachedCheckoutStage === "any" || reachedCheckoutStage == null) {
    return null;
  }
  return {
    key: "reachedCheckoutStage",
    value: reachedCheckoutStage,
    // Every flag on: this resolves a label for an already-selected stage, it
    // does not gate availability. Omitting a flag renders the chip blank.
    label:
      getConversionStageReachedList({
        hasCheckoutBlocks: true,
        hasOnsiteUpsell: true,
        hasTestPostPurchase: true,
      }).find((entry) => entry.key === reachedCheckoutStage)?.label || "",
  };
}

export function getUrlParamChip(
  urlParam: IExperienceDashboardFilters["urlParam"]
): AnalyticsActiveFilterType | null {
  if (!urlParam?.queryParam) {
    return null;
  }
  return { key: "urlParam", value: urlParam, label: "Url Param Enabled" };
}

export function getLandingPageChip(
  landingPage: IExperienceDashboardFilters["landingPage"]
): AnalyticsActiveFilterType | null {
  const isNullFilter = ["isNull", "isNotNull"].includes(
    landingPage?.landingPageUrlPathFilterType || ""
  );
  if (
    !landingPage?.landingPageUrlPathFilterType ||
    (!landingPage?.landingPageUrlPath && !isNullFilter)
  ) {
    return null;
  }
  return {
    key: "landingPage",
    value: landingPage.landingPageUrlPath || "null",
    label: "Landing Page Enabled",
  };
}

export function getLandingPageFiltersChip(
  landingPageFilters: LandingPageFilterItemType[] | undefined
): AnalyticsActiveFilterType | null {
  const activeFilters = (landingPageFilters ?? []).filter(
    (f) =>
      ["isNull", "isNotNull"].includes(f.filterType) ||
      (f.values && f.values[0])
  );
  if (!activeFilters.length) {
    return null;
  }
  const label =
    activeFilters.length === 1
      ? `Landing Page: ${activeFilters[0].filterType} ${activeFilters[0].values?.[0] ?? ""}`
      : `Landing Page: ${activeFilters.length} filters`;
  return {
    key: "landingPageFilters",
    value: landingPageFilters ?? [],
    label,
  };
}

export function getLandingPageTypesChip(
  landingPageTypes: LandingPageType[] | undefined
): AnalyticsActiveFilterType | null {
  if (!landingPageTypes?.length) {
    return null;
  }
  const labels = landingPageTypes.map(getLandingPageTypeLabel);
  const label =
    landingPageTypes.length > 3
      ? `Page Type: ${labels.slice(0, 3).join(", ")} +${labels.length - 3}`
      : `Page Type: ${labels.join(", ")}`;
  return { key: "landingPageTypes", value: landingPageTypes, label };
}

export function getUserBehaviorChip(
  userBehavior: IExperienceDashboardFilters["userBehavior"]
): AnalyticsActiveFilterType | null {
  if (
    !userBehavior?.addedToCart &&
    !userBehavior?.viewedCheckout &&
    !userBehavior?.viewedCollectionPage &&
    !userBehavior?.viewedProductPage
  ) {
    return null;
  }
  let filterString = "";
  let hasViewedProductPage = false;
  if (userBehavior?.viewedProductPage) {
    filterString = "Visited Product Page";
    hasViewedProductPage = true;
  }
  if (userBehavior?.viewedCollectionPage) {
    filterString = hasViewedProductPage
      ? `${filterString} & Collection`
      : "Visited Collection";
  }
  return { key: "userBehavior", value: userBehavior, label: filterString };
}

export function getMinMaxValueChip(
  key:
    | "minNetRevenue"
    | "maxNetRevenue"
    | "minShippingRevenue"
    | "maxShippingRevenue",
  value: number | null,
  storeCurrency: { code: string; locale: string }
): AnalyticsActiveFilterType | null {
  if (value === null) {
    return null;
  }
  return {
    key,
    value,
    label: `${camelToSentenceCase(key)}: ${lessOpinionatedCurrencyFormatter(value, storeCurrency)}`,
  };
}

export function getEndTimeChip(
  endTime: IExperienceDashboardFilters["endTime"],
  timezone: string,
  experience?: ExperienceFe
): AnalyticsActiveFilterType | null {
  if (!endTime) {
    return null;
  }
  // If this just reflects the experience's own natural end date, it isn't
  // an override worth surfacing as a filter chip.
  if (
    experience &&
    endTime === getLastEndedAtTs(experience.experienceActions)
  ) {
    return null;
  }
  return {
    key: "endTime",
    value: endTime,
    label: `End Time: ${fullDateTimeConverter(endTime, timezone, undefined, true) ?? ""}`,
  };
}

export function getStartTimeChip(
  startTime: IExperienceDashboardFilters["startTime"],
  timezone: string,
  experience?: ExperienceFe
): AnalyticsActiveFilterType | null {
  if (!startTime) {
    return null;
  }
  if (
    experience &&
    startTime === getFirstStartedAtTs(experience.experienceActions)
  ) {
    return null;
  }
  return {
    key: "startTime",
    value: startTime,
    label: `Start Time: ${fullDateTimeConverter(startTime, timezone, undefined, true) ?? ""}`,
  };
}
